import { GetStaticPaths, GetStaticProps } from "next"
import { useRouter } from "next/router"
import Head from "next/head"
import { getDriverProfile } from "@/lib/drivers/fetch"
import { DriverProfile } from "@/lib/drivers/types"
import { getActiveDriverIds } from "@/lib/drivers/active-profiles"
import { DriverProfileClient } from "@/components/drivers/driver-profile-client"
import PageNav from "@/storyblok/sections/PageNav"
import Footer from "@/storyblok/static/Footer"
import LocalPageHeader from "@/storyblok/sections/LocalPageHeader"
import Breadcrumb from "@/components/breadcrumbs"
import { ReviewsContextProvider } from "@/context/ReviewsContext"
import { getDriverExperience } from "@/lib/drivers/date-utils"
import { driverDescription } from "@/util/drivers/utils"

interface DriverProfilePageProps {
  profile: DriverProfile | null
}

const CACHE_TTL = 3600 // 1 hour in seconds

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const driverIds = await getActiveDriverIds()

    const paths = driverIds.map(({ ID }) => ({
      params: { id: ID.toString() },
    }))
    return {
      paths,
      fallback: true,
    }
  } catch (error) {
    console.error("[Build] Error in getStaticPaths:", error)
    throw error
  }
}

export const getStaticProps: GetStaticProps<DriverProfilePageProps> = async ({
  params,
}) => {
  try {
    const id = params?.id as string

    // Validate that id is a number and exists
    if (!id || isNaN(Number(id))) {
      return { notFound: true, revalidate: CACHE_TTL }
    }

    // Fetch active driver IDs to validate this ID
    const driverIds = await getActiveDriverIds()

    // Check if the ID exists in our valid driver IDs list
    const validId = driverIds.some(
      (driver) => driver.ID === id || driver.ID === Number(id).toString(),
    )
    if (!validId) {
      return { notFound: true, revalidate: CACHE_TTL }
    }

    const profile = await getDriverProfile(id)

    // Handle missing profile or data
    if (!profile?.data?.displayName) {
      return { notFound: true, revalidate: CACHE_TTL }
    }

    return {
      props: {
        profile,
      },
      revalidate: CACHE_TTL,
    }
  } catch (error) {
    console.error(
      `[Build] Error in getStaticProps for ID: ${params?.id}:`,
      error,
    )
    return { notFound: true, revalidate: CACHE_TTL }
  }
}

export default function DriverProfilePage({ profile }: DriverProfilePageProps) {
  const router = useRouter()

  // Handle loading state
  if (router.isFallback) {
    return (
      <>
        <PageNav noBlok buttonLabel="Get Transportation Quotes" />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
        </div>
        <Footer noBlok />
      </>
    )
  }

  // Handle error state
  if (!profile || !profile.data) {
    // Redirect to 404 page on client side if profile is not found
    if (typeof window !== "undefined") {
      router.push("/404")
      return null
    }

    return (
      <>
        <PageNav noBlok buttonLabel="Get Transportation Quotes" />
        <div className="flex items-center justify-center min-h-screen">
          <h1 className="text-2xl font-bold text-gray-800">Driver not found</h1>
        </div>
        <Footer noBlok />
      </>
    )
  }

  const { data } = profile
  const experience = getDriverExperience(data.joinedAt)
  const title = `${data.displayName} - Professional Driver | CitizenShipper`
  const description = `${data.displayName} - Professional driver with ${
    experience.years
  } years of experience. ${
    data.wonShipments
  } successful shipments. Rating: ${data.rating.toFixed(1)}/5 from ${
    data.ratingsCount
  } reviews.`

  const breadcrumbs = [
    { name: "Home", href: "/" },
    { name: "Drivers", href: "/drivers" },
    { name: data.displayName, href: `/drivers/${data.id}` },
  ]

  const headerProps = {
    heading: data.displayName,
    subheading: `Professional Transport Driver${
      experience.years > 1
        ? ` with ${experience.years} Years of Experience`
        : ""
    }`,
    description: driverDescription(data),
    hasLongDescription: true,
    buttonLabel: "Get Transportation Quotes",
    buttonLink: "shipments/new",
    image: {
      alt: data.displayName,
      filename:
        data.profilePhoto?.large?.replace("/m/", "") ||
        "https://a-us.storyblok.com/f/1002522/716x860/f51afecb18/header-hero.png",
    },
    smBgImage: {
      alt: data.displayName,
      filename:
        data.profilePhoto?.small?.replace("/m/", "") ||
        "https://a-us.storyblok.com/f/1002522/375x714/1ab232cc02/header-hero-sm.png",
    },
  }

  return (
    <ReviewsContextProvider>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {data.profilePhoto?.large && (
          <meta
            property="og:image"
            content={data.profilePhoto.large.replace("/m/", "")}
          />
        )}
        <link
          rel="canonical"
          href={`https://citizenshipper.com/drivers/${data.id}`}
        />
      </Head>
      <PageNav noBlok buttonLabel="Get Transportation Quotes" />
      <LocalPageHeader {...headerProps} items={breadcrumbs} />
      <div className="pt-8 lg:pt-16">
        <DriverProfileClient profile={profile} />
      </div>
      <Footer noBlok />
    </ReviewsContextProvider>
  )
}
